import type { ISchoolGradeDisplayConfiguration } from '@/core/school-grades/schoolGrade.types';
import { fetchGradeCalculationSettings } from '@/api/resources/gradeCalculationSettings.api';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { immutableSort } from 'common/utils/array.utils';
import { gradeCalculationKeys } from '../gradeCalculation.keys';

export const useGradeCalculationSettings = (tenantId: MaybeRefOrGetter<number | null | undefined>) => {
  const settingsQuery = useQueryWithToast({
    queryKey: computed(() => gradeCalculationKeys.settings(toValue(tenantId))),
    enabled: computed(() => !!toValue(tenantId)),
    queryFn: fetchGradeCalculationSettings,
  });

  const settings = computed(() => settingsQuery.data.value?.settings ?? null);

  const gradeTags = computed(() =>
    immutableSort(settingsQuery.data.value?.tags ?? [], (left, right) => left.sortOrder - right.sortOrder),
  );

  const defaultGradeTag = computed(() => gradeTags.value.find((tag) => tag.isDefault) ?? gradeTags.value.at(0) ?? null);

  const isGradeCalculationEnabled = computed(() => settings.value?.enabled ?? false);

  const gradeLevels = computed(() => settings.value?.gradeLevels ?? []);

  const enabledSchoolLevels = computed(() => settings.value?.schoolLevels ?? []);

  const lowerGradeValueIsBetter = computed(() => settings.value?.lowerGradeValueIsBetter ?? true);

  const isGradeCalculationEnabledForSchoolLevel = (schoolLevel: number | null | undefined): boolean => {
    if (!isGradeCalculationEnabled.value || schoolLevel == null) return false;
    return enabledSchoolLevels.value.includes(schoolLevel);
  };

  const gradeConfiguration = computed<ISchoolGradeDisplayConfiguration | null>(() => settings.value);

  return {
    settings,
    gradeConfiguration,
    suspense: settingsQuery.suspense,
    gradeTags,
    defaultGradeTag,
    gradeLevels,
    enabledSchoolLevels,
    lowerGradeValueIsBetter,
    isGradeCalculationEnabled,
    isGradeCalculationEnabledForSchoolLevel,
    isPending: settingsQuery.isPending,
    isError: settingsQuery.isError,
    refetch: settingsQuery.refetch,
  };
};
